import { Stack, SxProps, Theme } from '@mui/material';
import detectElementOverflow from 'detect-element-overflow';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'src/utils/hooks/use-debounce';
import useResizeObserver from 'use-resize-observer';

const HISTORY_SIZE = 3;

type OverflowListProps<T> = {
  items: T[];
  itemRenderer: (item: T) => React.ReactNode;
  showMoreElement?: (hiddenCount: number) => React.ReactNode;
  startElement?: React.ReactNode;
  endElement?: React.ReactNode;
  showAll?: boolean;
  sx?: SxProps<Theme>;
};

export const OverflowList = <T,>({
  items,
  itemRenderer,
  showMoreElement,
  startElement,
  endElement,
  showAll,
  sx,
}: OverflowListProps<T>) => {
  const [hiddenCount, setHiddenCount] = useState(0);

  const parentRef = useRef<HTMLDivElement | null>(null);

  const { width } = useResizeObserver({
    ref: parentRef as RefObject<Element>,
  });

  const hiddenCountHistory = useRef<number[]>([]);

  const detectOverflow = useDebounce(() => {
    if (showAll || !parentRef.current) {
      return;
    }
    const parent = parentRef.current;
    const children = Array.from(parent.children);
    let hidden = 0;

    for (let index = 0; index < children.length; index++) {
      const child = children[index];
      const collisions = detectElementOverflow(child as HTMLElement, parent);

      if (collisions?.collidedRight) {
        hidden = children.length - index;
        break;
      }
    }

    // prevent an infinite loop that can happen sometimes when
    // showMoreElement and the last item compete for the same place
    if (hidden !== hiddenCountHistory.current?.[0]) {
      setHiddenCount(hidden);
    }

    hiddenCountHistory.current.push(hidden);
    if (hiddenCountHistory.current.length > HISTORY_SIZE) {
      hiddenCountHistory.current.shift();
    }
  }, 500);

  useEffect(() => {
    detectOverflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  detectOverflow();

  return (
    <Stack
      ref={parentRef}
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        ...(!showAll && {
          flexWrap: 'nowrap',
        }),
        minWidth: 0,
        '& > *': {
          display: 'inline-block',
        },
        ...sx,
        ...(!showAll &&
          hiddenCount > 0 && {
            [`& > *:nth-of-type(n + ${items.length - hiddenCount + 2})`]: {
              opacity: 0,
              pointerEvents: 'none',
            },
          }),
      }}
    >
      {startElement}
      {items.map((item, index) => {
        return (
          <React.Fragment key={crypto.randomUUID()}>
            {!showAll && hiddenCount > 0 && index === items.length - hiddenCount && (
              <div>{showMoreElement?.(hiddenCount)}</div>
            )}
            <div>{itemRenderer(item)}</div>
          </React.Fragment>
        );
      })}
      {endElement}
    </Stack>
  );
};
